import styles from './AccordionGroup.module.scss';
import React, {
  Children,
  cloneElement,
  createRef,
  isValidElement,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const AccordionGroup = ({
  allowMultiple = false,
  as: Tag = 'div',
  className,
  children,
  defaultOpenIndex = -1,
  expanded = false,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultOpenIndex);
  const childrenRefs = useRef([]);

  if (childrenRefs.current?.length !== children?.length) {
    childrenRefs.current = Array(children?.length)
      .fill()
      .map((_, i) => childrenRefs.current?.[i] || createRef());
  }

  const accordionState = {
    allowMultiple,
    selectedIndex,
    setSelectedIndex,
  };

  return (
    <Tag className={classNames(styles.accordionGroup, className)}>
      {Children.toArray(children)?.map((child, index) => {
        // We can't filter out elements by this condition, because they need to
        // be rendered nonetheless.
        const isReactComponent =
          isValidElement(child) && typeof child?.type !== 'string';

        // We can't pass these props directly to cloneElement and conditionally
        // apply the accordionState/isOpen prop, because even if you pass them
        // as undefined it still throws en error in the console.
        let props = {
          index,
          ref: childrenRefs.current?.[index],
        };

        // Instead we only pass these props to components which actually have
        // them.
        if (isReactComponent) {
          props.accordionState = accordionState;
          props.isOpen = index === defaultOpenIndex || expanded;
        }

        return cloneElement(child, props);
      })}
    </Tag>
  );
};

AccordionGroup.propTypes = {
  allowMultiple: PropTypes.bool,
  as: PropTypes.elementType,
  children: PropTypes.arrayOf(PropTypes.node),
  className: PropTypes.string,
  defaultOpenIndex: PropTypes.number,
  expanded: PropTypes.bool,
};

export default AccordionGroup;
